import React, { useContext, useEffect, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Container } from '@mui/material';
import { addDueDateOffset, roundMinutes } from '@/_utils/date';
import { OrderPlacementConfig } from '@/__configs/config_types';
import Dialog from '../_common/Dialog';
import OPOrderStepper from './OPOrderStepper/OPOrderStepper';
import OPTitleBar from './OPTitleBar';
import { selectUser } from '@/redux/selectors/authSelectors';
import OPStep2FileSelection from './OPStep2FileSelection/FileSelections/OPStep2FileSelection';
import OPStep1OrderDetails from './OPStep1OrderDetails/OPStep1OrderDetails';
import OPReviewOrder from './OPReviewOrder/OPReviewOrder';
import { setStep, resumeDraft, clearOrderPlacement, toggleCancelationDialog, toggleFormIsIncomplete } from '@/redux/slices/orderPlacementSlice';
import {
  selectOpenCancelationDialog,
  selectFormIsIncomplete,
  selectFormIsDraft,
  selectDraftState,
  selectStep,
} from '@/redux/selectors/orderPlacementSelectors';
import OPOrderStepperButtons from './OPOrderStepperButtons';
import OPOrderConfirmation from './OPOrderConfirmation';
import { selectCustomersList } from '../../redux/selectors/constantsSelectors';
import { getProfileCustomers } from '../../redux/slices/constantsSlice';
import { clearTitle, resumeTitleSliceDraft } from '../../redux/slices/titleSlice';
import { getSchemaInitialValues, initializeSchema } from '../../_utils/profileUtils';
import { ConfigContext } from '../../contextAPI/configContext';
import OPStep2VersionSelection from './OPStep2VersionSelection/OPStep2VersionSelection';
import useProfile from '../_hooks/useProfile';
import { clearProfiles, getWorkflows } from '../../redux/slices/profileSlice';
import { useConfigValue } from '../_hooks/useConfigValue';
import Step2BulkSearch from './OPStep2VersionSelection/BulkSearch/Step2BulkSearch';
import ValueProvider from '@/contextAPI/fileContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectBillableCustomers } from '@/redux/selectors/dataTableFiltersSelectors';
import { setModuleState } from '@/redux/slices/dataTableSlice';
import { updateTimeZone } from '@/redux/slices/authSlice';
import OPStep2CSVVersionSelection from './OPStep2VersionSelection/CSVVersionSelection/OPStep2CSVVersionSelection';

const baseSchema = Yup.object().shape({
  description: Yup.string().required(),
  po_number: Yup.string(),
  managed_by: Yup.object().required(),
  billable_customer: Yup.mixed(), // Allow both strings and objects to handle draft resume
  jobs: Yup.array().of(
    Yup.object({
      customer: Yup.string().nullable().required(),
      profile: Yup.object().nullable().required(),
      due_date: Yup.date().nullable().required(),
      embargo_date: Yup.date().nullable()
    })
  )
});

const OrderPlacement = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const formikRef = useRef();
  const [navigateTo, setNavigateTo] = useState(null);
  const defaultTimeZoneConfig = useContext(ConfigContext)?.defaultTimeZone || 'UTC';
  const currentUser = useAppSelector(selectUser);
  const openCancelationDialog = useAppSelector(selectOpenCancelationDialog);
  const formIsIncomplete = useAppSelector(selectFormIsIncomplete);
  const formIsDraft = useAppSelector(selectFormIsDraft);
  const step = useAppSelector(selectStep);
  const customersList = useAppSelector(selectCustomersList);
  const draftState = useAppSelector(selectDraftState);
  const billableCustomers = useAppSelector(selectBillableCustomers);
  const customFields = useContext(ConfigContext)?.orderPlacementConfig?.addlFields?.orderDetails || {};
  const dueDateOffsetDays = useConfigValue<OrderPlacementConfig['dueDateOffsetDays']>(['orderPlacementConfig', 'dueDateOffsetDays']);
  const bulkTitleSearchOptions = useConfigValue<OrderPlacementConfig['bulkTitleSearchOptions']>(['orderPlacementConfig', 'bulkTitleSearchOptions']);
  const [validationSchema, setValidationSchema] = useState(baseSchema);
  const { titleSchema, isVersionBased, isBulkBased } = useProfile(formikRef.current?.values);
  const initialSchemaObj = {};

  Object.keys(customFields).forEach((key) => {
    if (!customFields[key].id) initialSchemaObj[key] = '';
  });

  const [initialValues, setInitialValues] = useState({
    description: '',
    po_number: '',
    managed_by: currentUser,
    billable_customer: '',
    ...initialSchemaObj,
    // componentized only allows a single profile selection,
    // but delivery will allow multiple profiles to be selected
    jobs: [
      {
        customer: customersList.length === 1 ? customersList[0] : '',
        profile: null,
        due_date: roundMinutes(dueDateOffsetDays ? addDueDateOffset(dueDateOffsetDays) : new Date()),
        embargo_date: null,
        title_inputs: {}
      }
    ],
    ...draftState.values
  });
  useEffect(() => {
    dispatch(getProfileCustomers());
    dispatch(getWorkflows());
    dispatch(setModuleState('orderPlacement'));
    return () => {
      dispatch(setModuleState(null));
      resetOrder();
    };
  }, []);

  const initializeTitleSchema = () => {
    if (Object.keys(titleSchema).length > 0 && step === 1) {
      const newSchema = Yup.object().shape({
        jobs: Yup.array().of(Yup.object({ title_inputs: Yup.object(initializeSchema(titleSchema)) }))
      });
      setValidationSchema(baseSchema.concat(newSchema));

      // set the initialValues for the step 2 schema
      const initialSchemaValues = getSchemaInitialValues(titleSchema);
      setInitialValues((prev) => ({
        ...prev,
        jobs: [{ ...prev.jobs[0], title_inputs: initialSchemaValues }]
      }));
    }
  };

  useEffect(() => {
    if (draftState?.values) {
      const processedValues = { ...draftState.values };
      
      // Handle billable_customer_customer from BE if it exists as an object
      if (processedValues.billable_customer_customer && typeof processedValues.billable_customer_customer === 'object') {
        processedValues.billable_customer = processedValues.billable_customer_customer;
        delete processedValues.billable_customer_customer;
      }
      // If billable_customer_customer doesn't exist, try to lookup billable_customer
      else if (typeof processedValues.billable_customer === 'string' && processedValues.billable_customer && billableCustomers.length > 0) {
        const matchingCustomer = billableCustomers.find(customer => customer.name === processedValues.billable_customer);
        if (matchingCustomer) {
          processedValues.billable_customer = matchingCustomer;
        }
        // If no match found, it can stay blank (will be handled as empty string)
      }

      if (processedValues.managed_by_user) {
        processedValues.managed_by = processedValues.managed_by_user;
        delete processedValues.managed_by_user;
      }
      
      if (!processedValues.managed_by && !processedValues.managed_by_user) {
        processedValues.managed_by = currentUser;
      }

      setInitialValues(processedValues);
      
      if (formikRef.current) {
        formikRef.current.resetForm({ values: processedValues });
        formikRef.current.validateForm();
        
        if (processedValues.billable_customer && typeof processedValues.billable_customer === 'object') {
          formikRef.current.setFieldValue('billable_customer', processedValues.billable_customer, false);
        }
      }
      
      dispatch(resumeDraft({ ...draftState, values: processedValues }));

      if (draftState.titleSlice) {
        dispatch(resumeTitleSliceDraft(draftState.titleSlice));
      }

      initializeTitleSchema();
    }
  }, [draftState?.values, billableCustomers]);

  useEffect(() => {
    // if we are on step 1, set to baseSchema that does not have the step 2 fields
    // because we only want step 2 fields validated after step 2
    if (step === 0) {
      const validationObj = {};
      Object.keys(customFields).forEach((key) => {
        if (!customFields[key].id) {
          if (customFields[key].required) {
            validationObj[key] = Yup.mixed().required();
          } else {
            validationObj[key] = Yup.mixed();
          }
        }
      });
      setValidationSchema(baseSchema.concat(Yup.object(validationObj)));
    }

    // if we are on step 2, append the schema for step 2 fields
    else initializeTitleSchema();
  }, [step]);

  const resetOrder = () => {
    dispatch(clearOrderPlacement());
    dispatch(clearTitle());
    dispatch(toggleCancelationDialog(false));
    dispatch(setStep(0));
    dispatch(clearProfiles());
    dispatch(updateTimeZone(defaultTimeZoneConfig));
  };

  const confirmCancel = () => {
    resetOrder();
    history.push(navigateTo ?? '/orders');
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return <OPStep1OrderDetails />;
      case 1:
        if (bulkTitleSearchOptions?.size && isVersionBased) {
          return <OPStep2CSVVersionSelection />;
        } else if (isVersionBased) {
          return <OPStep2VersionSelection />;
        } else if (isBulkBased) {
          return <Step2BulkSearch />;
        }
        return <OPStep2FileSelection />;
      case 2:
        return <OPReviewOrder />;
      case 3:
        return <OPOrderConfirmation isBulkBased={isBulkBased} />;
      default:
        return <OPOrderStepperButtons />;
    }
  };

  return (
    <>
      <ValueProvider>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          initialErrors
          validateForm
          validateOnBlur
          validateOnChange={false}
        >
          <div>
            <OPTitleBar />

            <Container>
              <OPOrderStepper step={step} isBulkBased={isBulkBased} />

              <Form noValidate>
                {renderStep()}

                <Dialog
                  open={openCancelationDialog}
                  title="Are you sure you want to leave this page?"
                  submitText="Yes, abandon changes"
                  onSubmit={confirmCancel}
                  cancelText="No, stay here"
                  maxWidth={480}
                  onCancel={() => {
                    dispatch(toggleFormIsIncomplete(true));
                    dispatch(toggleCancelationDialog(false));
                  }}
                >
                  It looks like you have unsaved changes. All order information will be lost if you navigate away.
                </Dialog>
              </Form>
            </Container>
            <Prompt
              when={formIsIncomplete && !formIsDraft}
              message={(location) => {
                dispatch(toggleCancelationDialog(true));
                dispatch(toggleFormIsIncomplete(false));
                setNavigateTo(location.pathname);
                return false;
              }}
            />
          </div>
        </Formik>
      </ValueProvider>
    </>
  );
};

export default OrderPlacement;
